// Modified PortfolioPrivacySettings.js
import React, { useState } from 'react';
import {
    Box,
    Typography,
    FormControl,
    RadioGroup,
    FormControlLabel,
    Radio,
    TextField,
    Button,
    List,
    ListItem,
    ListItemText,
    ListItemSecondaryAction,
    IconButton,
    Tooltip,
    FormGroup,
    Switch,
    FormHelperText
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import DeleteIcon from '@mui/icons-material/Delete';
import InfoIcon from '@mui/icons-material/Info';
import axios from '../config/axiosConfig';

const PortfolioPrivacySettings = ({ portfolio, onUpdate, onClose, userPublicNickname }) => {
    // Instead of updating immediately, store a draft of changes
    const [formData, setFormData] = useState({
        privacyLevel: portfolio.privacyLevel || 'private',
        sharedViewMode: portfolio.sharedViewMode || 'full',
        publicNickname: userPublicNickname || '',
        newViewerEmail: ''
    });

    // Track added/removed viewers separately to apply on save
    const [viewers, setViewers] = useState(portfolio.allowedViewers || []);
    const [copied, setCopied] = useState(false);

    const baseUrl = window.location.origin;
    const userNickname = formData.publicNickname || 'anonymous';
    const portfolioName = portfolio.name.toLowerCase().replace(/\s+/g, '-');
    const shareableUrl = formData.privacyLevel !== 'private'
        ? `${baseUrl}/portfolio/${userNickname}/${portfolioName}`
        : '';

    const handleInputChange = (e) => {
        const { name, value } = e.target;
        setFormData(prev => ({ ...prev, [name]: value }));
    };

    const handlePrivacyChange = (e) => {
        setFormData(prev => ({ ...prev, privacyLevel: e.target.value }));
    };

    const handleSharedViewModeChange = (e) => {
        const newSharedViewMode = e.target.checked ? 'full' : 'limited';
        setFormData(prev => ({ ...prev, sharedViewMode: newSharedViewMode }));
    };

    const handleAddViewer = () => {
        if (!formData.newViewerEmail) return;

        // Check if email already exists in viewers array
        if (viewers.some(viewer => viewer.email.toLowerCase() === formData.newViewerEmail.toLowerCase())) {
            // Show error or notification to user
            // option: setDuplicateError(true) and display an error message
            return;
        }

        // Add to local state only - will be saved on form submission
        const newViewer = {
            email: formData.newViewerEmail,
            dateAdded: new Date()
        };

        setViewers([...viewers, newViewer]);
        setFormData(prev => ({ ...prev, newViewerEmail: '' }));
    };

    const handleRemoveViewer = (email) => {
        setViewers(viewers.filter(viewer => viewer.email !== email));
    };

    const handleCopyLink = () => {
        navigator.clipboard.writeText(shareableUrl);
        setCopied(true);
        setTimeout(() => setCopied(false), 2000);
    };

    const handleSave = async () => {
        // Validate nickname if making public/shared
        if (formData.privacyLevel !== 'private' && !userPublicNickname) {
            alert('You need to set a public nickname in your profile settings before sharing portfolios.');
            return;
        }

        try {
            // First update privacy settings
            const response = await axios.put(`/api/portfolio/${portfolio._id}/privacy`, {
                privacyLevel: formData.privacyLevel,
                sharedViewMode: formData.sharedViewMode,
                publicNickname: userPublicNickname, // Always use the user's nickname
                allowedViewers: viewers // Send the updated viewers list
            });

            if (onUpdate) {
                onUpdate(response.data);
            }

            if (onClose) {
                onClose();
            }
        } catch (error) {
            console.error('Error saving privacy settings:', error);
            alert('Error saving settings. Please try again.');
        }
    };

    const handleClose = () => {
        if (onClose) {
            onClose();
        }
    };

    return (
        <Box sx={{ width: '100%' }}>
            <Typography variant="h6" gutterBottom>
                Privacy Settings
            </Typography>

            <FormControl component="fieldset" sx={{ width: '100%', mb: 3 }}>
                <RadioGroup
                    aria-label="privacy"
                    name="privacyLevel"
                    value={formData.privacyLevel}
                    onChange={handlePrivacyChange}
                >
                    <FormControlLabel
                        value="private"
                        control={<Radio />}
                        label={
                            <Box display="flex" alignItems="center">
                                Private
                                <Tooltip title="Only you can view this portfolio">
                                    <InfoIcon fontSize="small" sx={{ ml: 1 }} />
                                </Tooltip>
                            </Box>
                        }
                    />
                    <FormControlLabel
                        value="shared"
                        control={<Radio />}
                        label={
                            <Box display="flex" alignItems="center">
                                Shared with specific people
                                <Tooltip title="Only you and people you specifically invite can view this portfolio">
                                    <InfoIcon fontSize="small" sx={{ ml: 1 }} />
                                </Tooltip>
                            </Box>
                        }
                    />
                    <FormControlLabel
                        value="public"
                        control={<Radio />}
                        label={
                            <Box display="flex" alignItems="center">
                                Public
                                <Tooltip title="Anyone with the link can view this portfolio">
                                    <InfoIcon fontSize="small" sx={{ ml: 1 }} />
                                </Tooltip>
                            </Box>
                        }
                    />
                </RadioGroup>
            </FormControl>

            {/* Only show additional options if not private */}
            {formData.privacyLevel !== 'private' && (
                <>
                    {/* View Mode option (always show when not private) */}
                    {formData.privacyLevel !== 'private' && (
                        <Box mb={3}>
                            <FormGroup>
                                <FormControlLabel
                                    control={
                                        <Switch
                                            checked={formData.sharedViewMode === 'full'}
                                            onChange={handleSharedViewModeChange}
                                        />
                                    }
                                    label="Full Detail View"
                                />
                                <FormHelperText>
                                    {formData.sharedViewMode === 'full'
                                        ? "Viewers can see all details including quantities and values"
                                        : "Viewers will see limited details (symbols and prices only)"}
                                </FormHelperText>
                            </FormGroup>
                        </Box>
                    )}

                    {/* Read-only display of public nickname */}
                    <Box mb={3}>
                        <Typography variant="subtitle1">
                            Public Nickname
                        </Typography>
                        {userPublicNickname ? (
                            <Typography variant="body1" sx={{ mt: 1, fontWeight: 'medium' }}>
                                {userPublicNickname}
                            </Typography>
                        ) : (
                            <Typography variant="body1" sx={{ mt: 1, color: 'error.main', fontWeight: 'medium' }}>
                                Not set - Please set a nickname in your profile settings first
                            </Typography>
                        )}
                        <Typography variant="body2" color="textSecondary" sx={{ mt: 1 }}>
                            This name will be displayed to people viewing your portfolio. You can change it in your profile settings.
                        </Typography>
                    </Box>

                    <Box mb={3}>
                        <Typography variant="subtitle1">Shareable Link</Typography>
                        <Box display="flex" alignItems="center" mt={1}>
                            <TextField
                                fullWidth
                                value={shareableUrl}
                                variant="outlined"
                                size="small"
                                InputProps={{
                                    readOnly: true,
                                }}
                            />
                            <Button
                                variant="contained"
                                color="primary"
                                startIcon={<ContentCopyIcon />}
                                onClick={handleCopyLink}
                                sx={{ ml: 1 }}
                            >
                                {copied ? 'Copied!' : 'Copy'}
                            </Button>
                        </Box>
                    </Box>
                </>
            )}

            {/* People with access section (only for shared) */}
            {formData.privacyLevel === 'shared' && (
                <Box mb={3}>
                    <Typography variant="subtitle1">People with Access</Typography>
                    <Box display="flex" alignItems="center" mt={1}>
                        <TextField
                            fullWidth
                            name="newViewerEmail"
                            value={formData.newViewerEmail}
                            onChange={handleInputChange}
                            placeholder="Email address"
                            variant="outlined"
                            size="small"
                        />
                        <Button
                            variant="contained"
                            color="primary"
                            onClick={handleAddViewer}
                            sx={{ ml: 1 }}
                        >
                            Add
                        </Button>
                    </Box>

                    <List>
                        {viewers.map((viewer, index) => (
                            <ListItem key={index} divider={index < viewers.length - 1}>
                                <ListItemText primary={viewer.email} />
                                <ListItemSecondaryAction>
                                    <IconButton edge="end" onClick={() => handleRemoveViewer(viewer.email)}>
                                        <DeleteIcon />
                                    </IconButton>
                                </ListItemSecondaryAction>
                            </ListItem>
                        ))}
                    </List>
                </Box>
            )}

            {/* Updated bottom action buttons that are centered and the Save button is red */}
            <Box sx={{ display: 'flex', justifyContent: 'center', gap: 2, mt: 3 }}>
                <Button
                    variant="outlined"
                    onClick={handleClose}
                    sx={{ mr: 1 }}
                >
                    Close
                </Button>
                <Button
                    variant="contained"
                    onClick={handleSave}
                    color="error"
                >
                    Save
                </Button>
            </Box>
        </Box>
    );
};

export default PortfolioPrivacySettings;